import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconX, IconLoader2 } from '@tabler/icons-react';
import type {
  CheckInPaymentDeferMode,
  CheckInPaymentDeferRequest,
  CheckInPaymentLinkChannel,
  CheckInSession,
} from '@techgitter/contracts';
import './checkInDialog.css';

export interface DeferPaymentDialogProps {
  open: boolean;
  session: CheckInSession;
  onClose: () => void;
  onConfirm: (payload: CheckInPaymentDeferRequest) => Promise<void>;
}

interface ModeDef {
  mode: CheckInPaymentDeferMode;
  title: string;
  detail: string;
}

const MODES: ModeDef[] = [
  {
    mode: 'existing_customer',
    title: 'Defer — existing customer',
    detail: 'Send the invoice with a pay link. Diagnostics go ahead — nothing waits on payment.',
  },
  {
    mode: 'new_customer',
    title: 'Defer — new customer',
    detail:
      'Send the invoice with a pay link. The ticket is set to Waiting on Payment — diagnostics start once it is paid.',
  },
  {
    mode: 'not_required',
    title: 'Not required',
    detail: 'No diagnostics on this job (e.g. a screen repair).',
  },
];

/** Both Defer options send the invoice's pay link; Not Required sends nothing. */
const sendsLink = (mode: CheckInPaymentDeferMode): boolean => mode !== 'not_required';

/** Defer (Existing / New customer) or Not Required — replaces Override.
 *
 * Both Defer options send the invoice's pay link, so both need the RS
 * invoice and are disabled until `repairShoprInvoiceId` is set — the API
 * refuses on the same rule. A 409 ("not in Xero yet") leaves the dialog open
 * so reception can press again a moment later; nothing changed server-side. */
export const DeferPaymentDialog: React.FC<DeferPaymentDialogProps> = ({
  open,
  session,
  onClose,
  onConfirm,
}) => {
  const [mode, setMode] = useState<CheckInPaymentDeferMode | null>(null);
  const [channels, setChannels] = useState<CheckInPaymentLinkChannel[]>([]);
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const invoiceReady = !!session.repairShoprInvoiceId;

  const reset = () => {
    setMode(null);
    setNote('');
    setError(null);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  // Prefill from the session each time the dialog opens, so an edit made to
  // the customer's details since is what gets offered.
  useEffect(() => {
    if (!open) return;
    const sessionEmail = session.customerEmail ?? '';
    const sessionMobile = session.customerPhone ?? '';
    setEmail(sessionEmail);
    setMobile(sessionMobile);
    const initial: CheckInPaymentLinkChannel[] = [];
    if (sessionEmail) initial.push('email');
    if (sessionMobile) initial.push('sms');
    setChannels(initial);
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const toggleChannel = (channel: CheckInPaymentLinkChannel) => {
    setChannels(prev => (prev.includes(channel) ? prev.filter(c => c !== channel) : [...prev, channel]));
    if (error) setError(null);
  };

  const validate = (): string | null => {
    if (!mode) return 'Choose an option.';
    if (!sendsLink(mode)) return null;
    if (!invoiceReady) return "The invoice doesn't exist yet — create tickets first.";
    if (channels.length === 0) return 'Choose email, SMS or both.';
    if (channels.includes('email') && !email.trim().includes('@')) return 'Enter an email address.';
    if (channels.includes('sms') && !mobile.trim()) return 'Enter a mobile number.';
    return null;
  };

  const handleSubmit = async () => {
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    const payload: CheckInPaymentDeferRequest = { mode: mode!, note: note.trim() || undefined };
    if (sendsLink(mode!)) {
      payload.channels = channels;
      if (channels.includes('email')) payload.email = email.trim();
      if (channels.includes('sms')) payload.mobile = mobile.trim();
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(payload);
      reset();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBackdropMouseDown = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !submitting) handleClose();
  };

  const confirmLabel = mode && sendsLink(mode) ? 'Send invoice' : 'Confirm';

  return createPortal(
    <div className="modal-backdrop" onMouseDown={handleBackdropMouseDown}>
      <div className="ci-dialog" role="dialog" aria-modal="true" aria-label="Defer or not required">
        <div className="ci-dialog__header">
          <span className="ci-dialog__title">Defer / Not Required</span>
          <button
            type="button"
            className="ci-dialog__close"
            onClick={handleClose}
            disabled={submitting}
            aria-label="Close"
          >
            <IconX size={14} stroke={1.5} />
          </button>
        </div>

        <div className="ci-dialog__body">
          <div className="ci-dialog__options" role="radiogroup" aria-label="Payment option">
            {MODES.map(def => {
              const disabled = submitting || (sendsLink(def.mode) && !invoiceReady);
              const selected = mode === def.mode;
              return (
                <label
                  key={def.mode}
                  className={
                    'ci-dialog__option'
                    + (selected ? ' ci-dialog__option--selected' : '')
                    + (disabled ? ' ci-dialog__option--disabled' : '')
                  }
                >
                  <input
                    type="radio"
                    name="defer-mode"
                    value={def.mode}
                    checked={selected}
                    disabled={disabled}
                    onChange={() => {
                      setMode(def.mode);
                      if (error) setError(null);
                    }}
                  />
                  <span className="ci-dialog__option-text">
                    <span className="ci-dialog__option-title">{def.title}</span>
                    <span className="ci-dialog__option-detail">
                      {sendsLink(def.mode) && !invoiceReady
                        ? "Create tickets first — the invoice doesn't exist yet."
                        : def.detail}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>

          {mode && sendsLink(mode) && (
            <>
              <span className="ci-dialog__field-label">Send the invoice by</span>
              <label className="ci-dialog__check ci-dialog__check--tight">
                <input
                  type="checkbox"
                  checked={channels.includes('email')}
                  onChange={() => toggleChannel('email')}
                  disabled={submitting}
                />
                Email
              </label>
              {channels.includes('email') && (
                <input
                  className="ci-dialog__input"
                  type="email"
                  aria-label="Email address"
                  placeholder="customer@example.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  disabled={submitting}
                />
              )}
              <label className="ci-dialog__check ci-dialog__check--tight">
                <input
                  type="checkbox"
                  checked={channels.includes('sms')}
                  onChange={() => toggleChannel('sms')}
                  disabled={submitting}
                />
                SMS
              </label>
              {channels.includes('sms') && (
                <input
                  className="ci-dialog__input"
                  type="tel"
                  aria-label="Mobile number"
                  placeholder="04xx xxx xxx"
                  value={mobile}
                  onChange={e => setMobile(e.target.value)}
                  disabled={submitting}
                />
              )}
            </>
          )}

          {mode && (
            <>
              <label className="ci-dialog__field-label" htmlFor="defer-note">
                Note (optional)
              </label>
              <textarea
                id="defer-note"
                className="ci-dialog__textarea"
                placeholder="Recorded in the audit log"
                value={note}
                onChange={e => setNote(e.target.value)}
                disabled={submitting}
              />
            </>
          )}

          {error && <div className="ci-dialog__error">{error}</div>}
        </div>

        <div className="ci-dialog__footer">
          <button type="button" className="ci-dialog__cancel" onClick={handleClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="ci-dialog__confirm ci-dialog__confirm--primary"
            onClick={handleSubmit}
            disabled={submitting || !mode}
          >
            {submitting && <IconLoader2 size={14} stroke={1.5} className="ci-dialog__spin" />}
            {submitting ? 'Saving…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};
